import type { CaseInputs, Formula, SchemaEntry, View } from './types';
import { LOCATION_VIEWS } from './types';
import { indexSchema } from './schema';

export interface IgnoreGroup {
    parent: string;
    members: string[];
}

const EXCLUDED_IGNORE_PARENTS = new Set([
    'bs.less_accumulated_depreciation',
    'bs.less_accumulated_amortization',
]);

const CASH_PLUG_KEY = 'bs.cash_bank_accounts_and_petty_cash';

function sumMemberKeys(formula: Formula): string[] | null {
    if (typeof formula !== 'object' || formula.op !== 'sum') {
        return null;
    }
    const keys: string[] = [];
    for (const item of formula.of) {
        if (typeof item !== 'string') {
            return null;
        }
        keys.push(item);
    }
    return keys;
}

function isEligibleGroup(
    schemaByKey: Record<string, SchemaEntry>,
    parent: string,
    members: string[],
): boolean {
    if (EXCLUDED_IGNORE_PARENTS.has(parent)) {
        return false;
    }
    if (members.length < 2) {
        return false;
    }
    if (members.some((key) => key === CASH_PLUG_KEY)) {
        return false;
    }
    const parentEntry = schemaByKey[parent];
    if (!parentEntry || parentEntry.kind !== 'subtotal') {
        return false;
    }
    return members.every((key) => schemaByKey[key]?.kind === 'input');
}

export function listEligibleIgnoreGroups(schema: SchemaEntry[]): IgnoreGroup[] {
    const { byKey } = indexSchema(schema);
    const groups: IgnoreGroup[] = [];
    for (const entry of schema) {
        if (entry.kind !== 'subtotal' || !entry.formula) {
            continue;
        }
        const members = sumMemberKeys(entry.formula);
        if (!members) {
            continue;
        }
        if (isEligibleGroup(byKey, entry.key, members)) {
            groups.push({ parent: entry.key, members });
        }
    }
    return groups;
}

export function ignoreGroupForLine(schema: SchemaEntry[], lineKey: string): IgnoreGroup | null {
    for (const group of listEligibleIgnoreGroups(schema)) {
        if (group.members.includes(lineKey)) {
            return group;
        }
    }
    return null;
}

export function ignoreGroupForParent(schema: SchemaEntry[], parentKey: string): IgnoreGroup | null {
    for (const group of listEligibleIgnoreGroups(schema)) {
        if (group.parent === parentKey) {
            return group;
        }
    }
    return null;
}

export function isGroupIgnored(inputs: CaseInputs, view: View, group: IgnoreGroup): boolean {
    for (const member of group.members) {
        if (inputs.drivers[view]?.[member]?.active !== 'ignore') {
            return false;
        }
    }
    return true;
}

export function isGroupPartiallyIgnored(
    inputs: CaseInputs,
    view: View,
    group: IgnoreGroup,
): boolean {
    let ignoredCount = 0;
    for (const member of group.members) {
        if (inputs.drivers[view]?.[member]?.active === 'ignore') {
            ignoredCount += 1;
        }
    }
    return ignoredCount > 0 && ignoredCount < group.members.length;
}

export function parentGroupIfIgnored(
    schema: SchemaEntry[],
    inputs: CaseInputs,
    view: View,
    parentKey: string,
): IgnoreGroup | null {
    const group = ignoreGroupForParent(schema, parentKey);
    if (!group || !isGroupIgnored(inputs, view, group)) {
        return null;
    }
    return group;
}

export function isGroupIgnoredAtAnyLocation(inputs: CaseInputs, group: IgnoreGroup): boolean {
    for (const view of LOCATION_VIEWS) {
        if (isGroupIgnored(inputs, view, group)) {
            return true;
        }
    }
    return false;
}
